import React, { useEffect, useMemo, useState } from 'react';
import type {
  PriceWidgetInstanceConfig,
  PriceData,
  PriceChartRange,
  PriceDisplayMode,
  PriceHistoryRow,
} from './types';
import { PRICE_DEFAULT_PROVIDER, PRICE_RANGE_OPTIONS } from './types';
import {
  formatDate,
  formatFloat,
  formatReturn,
  formatVolume,
  axisPrice,
  buildTimeAxisFormatter,
} from '../../../design';
import { rebaseToPercent } from '../../../blocks/charts/chartUtils';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, KeyMetricBadge } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { LineChartHoverDatum, LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';
import { DEFAULT_COLORS, SEMANTIC_CHART_COLORS } from '../../../design';
import { dataSourceService } from '../../../../../services';

// Sample data shown during dashboard construction when no asset or preview ticker is assigned.
const SAMPLE_DATA: PriceData = {
  ticker: 'SAMPLE',
  latest_price: 187.44,
  latest_volume: 52341200,
  adtv_3m: 61234500,
  price_history: [
    { date: '2020-05-01', price: 118.14, volume: 35800000, adtv: 34500000 },
    { date: '2021-01-15', price: 132.8, volume: 40200000, adtv: 36800000 },
    { date: '2021-11-02', price: 149.6, volume: 44600000, adtv: 39100000 },
    { date: '2022-07-12', price: 161.95, volume: 38900000, adtv: 40800000 },
    { date: '2023-03-27', price: 154.22, volume: 43100000, adtv: 42500000 },
    { date: '2023-12-08', price: 169.7, volume: 45800000, adtv: 44100000 },
    { date: '2024-08-19', price: 178.55, volume: 49100000, adtv: 46800000 },
    { date: '2025-03-21', price: 187.44, volume: 52341200, adtv: 61234500 },
  ],
  index_ticker: 'SPY',
  index_price_history: [
    { date: '2020-05-01', price: 282.79 },
    { date: '2021-01-15', price: 375.7 },
    { date: '2021-11-02', price: 461.02 },
    { date: '2022-07-12', price: 381.14 },
    { date: '2023-03-27', price: 396.49 },
    { date: '2023-12-08', price: 459.1 },
    { date: '2024-08-19', price: 546.12 },
    { date: '2025-03-21', price: 510.8 },
  ],
};

interface PriceWidgetRendererProps {
  config: PriceWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

interface ProviderAvailability {
  registeredProviders: string[];
  userProviders: string[];
}

const buildEmptyPriceData = (ticker: string, indexTicker?: string): PriceData => ({
  ticker,
  latest_price: null,
  latest_volume: null,
  adtv_3m: null,
  price_history: null,
  index_ticker: indexTicker || null,
  index_price_history: null,
});

const sortByDateAsc = <T extends { date: string }>(rows: T[] | null | undefined): T[] => (
  rows ? [...rows].sort((a, b) => a.date.localeCompare(b.date)) : []
);

const returnTone = (value: number | null): string => {
  if (value == null) return 'border-neutral-200 bg-neutral-100 text-neutral-600';
  if (value > 0) return 'border-success-300 bg-success-50 text-success-700';
  if (value < 0) return 'border-danger-300 bg-danger-50 text-danger-700';
  return 'border-neutral-200 bg-neutral-100 text-neutral-600';
};

const PriceWidgetRenderer: React.FC<PriceWidgetRendererProps> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const configuredTicker = typeof config.ticker === 'string' && config.ticker.trim()
    ? config.ticker.trim().toUpperCase()
    : undefined;
  const effectiveTicker = uid || configuredTicker;
  const isPreview = !effectiveTicker;

  const [selectedProvider, setSelectedProvider] = useState((config.provider || PRICE_DEFAULT_PROVIDER).toUpperCase());
  const [selectedRange, setSelectedRange] = useState<PriceChartRange>(config.defaultRange || '3M');
  // Relative (% change) is the default lens when a comparison index is configured.
  const [displayMode, setDisplayMode] = useState<PriceDisplayMode>(
    config.displayMode || (config.index ? 'relative' : 'price'),
  );
  // Inline comparison-ticker editing: draft tracks the input, indexTicker is the
  // committed value that drives the fetch.
  const [indexDraft, setIndexDraft] = useState(config.index || '');
  const [indexTicker, setIndexTicker] = useState(config.index || '');
  const [providerAvailability, setProviderAvailability] = useState<ProviderAvailability>({
    registeredProviders: ['YFINANCE', 'FMP'],
    userProviders: [],
  });
  const [providerAvailabilityChecked, setProviderAvailabilityChecked] = useState(false);
  const [providerAvailabilityResolved, setProviderAvailabilityResolved] = useState(false);
  const [hasAutoSelectedProvider, setHasAutoSelectedProvider] = useState(false);
  const [hoveredDatum, setHoveredDatum] = useState<LineChartHoverDatum | null>(null);

  useEffect(() => {
    setSelectedProvider((config.provider || PRICE_DEFAULT_PROVIDER).toUpperCase());
    setHasAutoSelectedProvider(false);
  }, [config.provider]);

  useEffect(() => {
    setSelectedRange(config.defaultRange || '3M');
  }, [config.defaultRange]);

  useEffect(() => {
    setDisplayMode(config.displayMode || (config.index ? 'relative' : 'price'));
  }, [config.displayMode, config.index]);

  useEffect(() => {
    setIndexDraft(config.index || '');
    setIndexTicker(config.index || '');
  }, [config.index]);

  const commitIndexDraft = () => {
    const next = indexDraft.trim().toUpperCase();
    setIndexDraft(next);
    setIndexTicker(next);
  };

  useEffect(() => {
    let active = true;

    const loadProviders = async () => {
      try {
        const result = await dataSourceService.getAvailableProviders();
        if (!active) return;
        setProviderAvailability({
          registeredProviders: result.registered_providers?.length ? result.registered_providers : ['YFINANCE', 'FMP'],
          userProviders: result.user_providers || [],
        });
        setProviderAvailabilityChecked(true);
        setProviderAvailabilityResolved(true);
      } catch (err) {
        if (!active) return;
        setProviderAvailability({
          registeredProviders: ['YFINANCE', 'FMP'],
          userProviders: [],
        });
        setProviderAvailabilityChecked(true);
        setProviderAvailabilityResolved(false);
      }
    };

    loadProviders();

    return () => {
      active = false;
    };
  }, []);

  const queryConfig = useMemo(
    () => ({
      provider: selectedProvider,
      index: indexTicker || undefined,
    }),
    [selectedProvider, indexTicker],
  );

  const providerOptions = providerAvailability.registeredProviders.length > 0
    ? [PRICE_DEFAULT_PROVIDER, ...providerAvailability.registeredProviders]
    : [PRICE_DEFAULT_PROVIDER, 'YFINANCE', 'FMP'];
  const providerConfigured = selectedProvider === PRICE_DEFAULT_PROVIDER
    || providerAvailability.userProviders.includes(selectedProvider);
  const missingProviderCredentials = !!effectiveTicker
    && providerAvailabilityResolved
    && providerOptions.includes(selectedProvider)
    && !providerConfigured;
  const shouldFetchLiveData = !!effectiveTicker
    && providerAvailabilityChecked
    && !missingProviderCredentials;

  useEffect(() => {
    if (hasAutoSelectedProvider || !providerAvailabilityChecked) return;
    if (selectedProvider === PRICE_DEFAULT_PROVIDER) {
      setHasAutoSelectedProvider(true);
      return;
    }
    if (providerAvailability.userProviders.length === 0) return;
    if (providerAvailability.userProviders.includes(selectedProvider)) {
      setHasAutoSelectedProvider(true);
      return;
    }

    const preferredProvider = typeof config.provider === 'string' ? config.provider.toUpperCase() : '';
    const nextProvider = providerAvailability.userProviders.includes(preferredProvider)
      ? preferredProvider
      : providerAvailability.userProviders[0];

    if (nextProvider && nextProvider !== selectedProvider) {
      setSelectedProvider(nextProvider);
    }
    setHasAutoSelectedProvider(true);
  }, [
    config.provider,
    hasAutoSelectedProvider,
    providerAvailability.userProviders,
    providerAvailabilityChecked,
    selectedProvider,
  ]);

  const { data, loading, error, refetch } = useDataProviderWidget<PriceData>(
    'price',
    effectiveTicker,
    queryConfig as unknown as Record<string, unknown>,
    {
      enabled: shouldFetchLiveData,
      widgetInstanceId,
    },
  );

  const displayData = useMemo<PriceData | null>(() => {
    if (!effectiveTicker) return SAMPLE_DATA;
    if (data) return data;
    if (missingProviderCredentials) {
      return buildEmptyPriceData(effectiveTicker, indexTicker);
    }
    return null;
  }, [indexTicker, data, effectiveTicker, missingProviderCredentials]);

  const fullPriceHistory = useMemo<PriceHistoryRow[]>(
    () => sortByDateAsc(displayData?.price_history),
    [displayData],
  );

  const visiblePriceHistory = useMemo(() => {
    if (selectedRange === 'YTD') {
      // Jan 1 of the latest data year — falls back to all rows when empty.
      const lastDate = fullPriceHistory[fullPriceHistory.length - 1]?.date;
      if (!lastDate) return fullPriceHistory;
      const jan1 = `${lastDate.slice(0, 4)}-01-01`;
      return fullPriceHistory.filter((row) => row.date >= jan1);
    }
    const rangeConfig = PRICE_RANGE_OPTIONS.find((option) => option.value === selectedRange);
    if (!rangeConfig?.tradingDays) return fullPriceHistory;
    return fullPriceHistory.slice(-rangeConfig.tradingDays);
  }, [fullPriceHistory, selectedRange]);

  const indexHistoryByDate = useMemo(() => {
    const rows = sortByDateAsc(displayData?.index_price_history);
    return new Map(rows.map((row) => [row.date, row.price]));
  }, [displayData]);

  // Date → close lookup over the visible window, so the header can show the
  // actual price at a hovered point (relative mode plots % change, not price).
  const priceByDate = useMemo(
    () => new Map(visiblePriceHistory.map((row) => [row.date, row.price])),
    [visiblePriceHistory],
  );

  const absoluteChartData = useMemo(() => (
    visiblePriceHistory.map((row) => {
      const point: Record<string, unknown> = {
        date: row.date,
        price: row.price,
        volume: row.volume,
      };
      const indexPrice = indexHistoryByDate.get(row.date);
      if (typeof indexPrice === 'number') {
        point.indexPrice = indexPrice;
      }
      return point;
    })
  ), [visiblePriceHistory, indexHistoryByDate]);

  const hasIndex = !!(displayData?.index_ticker && displayData?.index_price_history?.length);
  const isRelative = displayMode === 'relative';

  // Relative mode: rebase both series to % change from the visible window's
  // start, plus the running SPREAD (ticker % minus index %) — the shaded band.
  const chartData = useMemo<Array<Record<string, unknown>>>(() => {
    if (!isRelative) return absoluteChartData;
    return rebaseToPercent(absoluteChartData, ['price', 'indexPrice']).map((row) => ({
      ...row,
      spread:
        typeof row.price === 'number' && typeof row.indexPrice === 'number'
          ? (row.price as number) - (row.indexPrice as number)
          : null,
    }));
  }, [absoluteChartData, isRelative]);

  const latestSpread = useMemo(() => {
    if (!isRelative || !hasIndex) return null;
    for (let i = chartData.length - 1; i >= 0; i -= 1) {
      const value = chartData[i].spread;
      if (typeof value === 'number') return value;
    }
    return null;
  }, [chartData, hasIndex, isRelative]);

  const series = useMemo<LineChartSeries[]>(() => {
    if (isRelative && hasIndex) {
      // Rebased % lines share the left axis; the SPREAD rides its own (tighter)
      // right axis and renders as a soft amber BAND (area fill from its zero
      // baseline) so it stays readable instead of a flat smear.
      return [
        { yKey: 'price', yName: displayData?.ticker || effectiveTicker || 'Price', color: DEFAULT_COLORS[0], markers: false, axis: 'rel' },
        { yKey: 'indexPrice', yName: displayData?.index_ticker || 'Index', color: DEFAULT_COLORS[1], markers: false, axis: 'rel' },
        { yKey: 'spread', yName: 'Spread', color: SEMANTIC_CHART_COLORS.caution, markers: false, axis: 'sprd', fill: true, fillOpacity: 0.18 },
      ];
    }

    const result: LineChartSeries[] = [
      {
        yKey: 'price',
        yName: displayData?.ticker || effectiveTicker || 'Price',
        color: DEFAULT_COLORS[0],
        markers: false,
      },
    ];

    if (hasIndex) {
      result.push({
        yKey: 'indexPrice',
        yName: displayData?.index_ticker || 'Index',
        color: DEFAULT_COLORS[1],
        markers: false,
      });
    }

    return result;
  }, [displayData, effectiveTicker, hasIndex, isRelative]);

  // Bottom legend strip — names only; the header readouts carry the live values.
  const legendSeries = useMemo<ChartLegendSeries[]>(() => series.map((s) => ({
    key: s.yKey,
    name: s.yName,
    color: s.color || DEFAULT_COLORS[0],
  })), [series]);

  const dates = useMemo(() => chartData.map((row) => row.date as string), [chartData]);
  const timeFormatter = useMemo(() => buildTimeAxisFormatter(dates), [dates]);
  const latestHistoryDate = fullPriceHistory.length > 0
    ? fullPriceHistory[fullPriceHistory.length - 1].date
    : undefined;
  const rangeReturn = useMemo(() => {
    if (visiblePriceHistory.length < 2) return null;
    const first = visiblePriceHistory[0]?.price;
    const last = visiblePriceHistory[visiblePriceHistory.length - 1]?.price;
    if (!first || !last) return null;
    return (last - first) / first;
  }, [visiblePriceHistory]);
  const rangeLabel = PRICE_RANGE_OPTIONS.find((option) => option.value === selectedRange)?.label || selectedRange;
  // The header is a complete snapshot at rest — latest close + endpoint return,
  // readable without hovering — and tracks the cursor point on hover.
  const hoveredValue = typeof hoveredDatum?.yValue === 'number' ? hoveredDatum.yValue : null;
  const hoveredDate = typeof hoveredDatum?.xValue === 'string' ? hoveredDatum.xValue : undefined;
  const headerPriceDate = hoveredDate ?? latestHistoryDate;
  // Relative mode plots a % (not a price), so look up the actual close at the
  // hovered date — keeps the Price stat coherent with the As-of date.
  const headerPrice = isRelative
    ? (hoveredDate ? priceByDate.get(hoveredDate) ?? displayData?.latest_price ?? null : displayData?.latest_price ?? null)
    : hoveredValue ?? displayData?.latest_price ?? null;
  // % Change defaults to the window's endpoint return (the rebased line's last
  // point) so it's populated at rest, then follows the cursor on hover.
  const pctChange = hoveredValue ?? rangeReturn;
  // Spread likewise: the hovered point's spread (same row as the price series)
  // when hovering, else the latest — so every header stat reflects one point.
  const hoveredSpread = typeof (hoveredDatum?.datum as Record<string, unknown> | undefined)?.spread === 'number'
    ? ((hoveredDatum!.datum as Record<string, number>).spread)
    : null;
  const headerSpread = hoveredSpread ?? latestSpread;

  const spec: ChartSpec = {
    chartKind: 'line',
    rows: chartData,
    xKey: 'date',
    lineSeries: series,
    enableCrosshair: true,
    hoverSeriesKey: 'price',
    onDatumHover: setHoveredDatum,
    xLabelFormatter: timeFormatter,
    xTitle: 'Date',
    ...(isRelative && hasIndex
      ? {
          yLabelFormatters: {
            rel: (params) => formatReturn(Number(params.value), 0),
            sprd: (params) => formatReturn(Number(params.value), 0),
          },
          yTitles: { rel: 'Return (%)', sprd: 'Spread (%)' },
          yAxisColors: { sprd: SEMANTIC_CHART_COLORS.caution },
          zeroLineAxes: ['sprd'],
          // The spread axis carries no gridlines and its nice range often runs
          // wider than the band, so the default ~5-tick step reads sparse —
          // pack in more % labels (gridline-less, so no added clutter).
          yTickCounts: { sprd: 8 },
          // All three series are rebased to 0% at the window start, so the
          // spread's zero baseline must sit at the same height as the return
          // lines' zero — anchor the spread axis's zero to the return axis.
          alignZeroAxes: ['sprd'],
        }
      : {
          yLabelFormatter: isRelative
            ? (params) => formatReturn(Number(params.value), 0)
            : axisPrice,
          yTitles: { a: isRelative ? 'Return (%)' : 'Price ($)' },
        }),
    legendSeries,
    footnote: 'Source: FMP',
    emptyMessage: 'No price history available',
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      {/* Title block (flex-1) — title + return badge, range/as-of subtitle beneath.
          Mirrors the Short Interest header skeleton: flex-1 packs the comparison,
          stats and controls into a group on the right. */}
      <div className="min-w-0 mr-1 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-neutral-900 truncate">Price Performance</h3>
          <span className={`inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${returnTone(rangeReturn)}`}>
            {rangeReturn == null ? '--' : `${rangeReturn >= 0 ? '+' : ''}${(rangeReturn * 100).toFixed(1)}%`}
          </span>
        </div>
        <div className="text-[11px] text-neutral-400 truncate">
          <span className="uppercase tracking-wide">{rangeLabel} Return · As of </span>
          {formatDate(headerPriceDate)}
        </div>
      </div>

      {/* Comparison control (AMD vs SPY) — first of the right-side stat group. */}
      <p className="flex shrink-0 items-center gap-1 text-sm text-neutral-500">
        <span>{displayData?.ticker || effectiveTicker || 'Preview'}</span>
        <span>vs</span>
        <input
          type="text"
          value={indexDraft}
          onChange={(e) => setIndexDraft(e.target.value.toUpperCase())}
          onBlur={commitIndexDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          placeholder="INDEX"
          aria-label="Comparison ticker"
          className="w-16 rounded border border-dashed border-neutral-300 bg-transparent px-1 py-0 text-sm font-medium uppercase text-neutral-700 placeholder:text-neutral-300 focus:border-primary-400 focus:outline-none"
        />
      </p>

      {/* Stat chips — Chart-DS KeyMetricBadge, direct siblings like Short Interest.
          The % Change slot is always rendered in relative mode and reserves a fixed
          width so the header doesn't reflow on hover. */}
      {isRelative && (
        <KeyMetricBadge
          label="% Change"
          value={formatReturn(pctChange)}
          valueClassName="inline-block min-w-[3.5rem] text-right tabular-nums"
        />
      )}
      {isRelative && hasIndex && (
        <KeyMetricBadge label="Spread" value={formatReturn(headerSpread)} />
      )}
      <KeyMetricBadge label="Price" value={formatFloat(headerPrice)} />
      <KeyMetricBadge label="Vol" value={formatVolume(displayData?.latest_volume ?? null)} />
      <KeyMetricBadge label="ADTV 3M" value={formatVolume(displayData?.adtv_3m ?? null)} />

      {/* Controls pushed right (ml-auto) — display-mode + range capsules. */}
      <div className="ml-auto flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1">
          {([
            { value: 'price', label: 'Price' },
            { value: 'relative', label: '%' },
          ] as Array<{ value: PriceDisplayMode; label: string }>).map((option) => {
            const isActive = displayMode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setDisplayMode(option.value)}
                className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1">
          {PRICE_RANGE_OPTIONS.map((option) => {
            const isActive = selectedRange === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedRange(option.value)}
                className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const missingCredentialsContent = (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-sm rounded-xl border border-warning-300 bg-warning-50 px-4 py-4 text-center">
        <p className="text-sm font-semibold text-warning-800">Missing credentials for this provider</p>
        <p className="mt-1 text-xs text-warning-700">
          Add credentials for {selectedProvider} in Settings before loading live price data.
        </p>
      </div>
    </div>
  );

  // Card chrome comes from ChartWidget's uniform defaults — every chart
  // widget shares the same bordered, tinted container.
  return (
    <ChartWidget
      loading={loading || (!!effectiveTicker && !providerAvailabilityChecked)}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      header={header}
      emptyContent={missingProviderCredentials ? missingCredentialsContent : undefined}
    />
  );
};

export default PriceWidgetRenderer;
